import { Injector } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { filter, map, pairwise } from "rxjs/operators";

import { Converter } from "../interface/converter.interface";
import { FormErrorMessage } from "../interface/type/form-error-message.type";
import { FormSpecification } from "../interface/type/form-specification.type";
import { NetChange } from "../interface/type/net-change.type";
import { ValidatorFactory } from "../interface/validator-factory.interface";
import { Optional } from "../support/optional.model";
import { Logger } from "./logger.model";

	/**
	 * @description
	 *	Formulario avanzado, que permite utilizar una especificación para
	 *	construir los controles, sus errores y validadores asociados. A su
	 *	vez, se proveen algunos observables que simplifican la gestión y la
	 *	consistencia de la UI a lo largo de las diferentes vistas disponibles.
	 *
	 *	El objetivo principal de este modelo es simplificar la construcción de
	 *	los distintos formularios, centralizar sus características, e
	 *	incrementar la reusabildiad de sus validadores.
	 */

export class AdvancedForm {

	private _isChanging: BehaviorSubject<any>;
	private _status: BehaviorSubject<string>;

	private converters: Map<string, Converter<any, any>>;
	private form: FormGroup;
	private injector: Injector;
	private logger: Logger;
	private specification: FormSpecification;

	constructor(builder: AdvancedFormBuilder) {
		this.converters = new Map<string, Converter<any, any>>();
		this.injector = builder._injector;
		this.logger = builder._logger;
		this.specification = builder._specification;
		const controls = {};
		const crossValidators = [];
		const crossAsyncValidators = [];
		for (const [key, configuration] of Object.entries(this.specification)) {
			const converter: Converter<any, any> = new configuration.converter(this.injector);
			this.converters.set(key, converter);
			const factory: ValidatorFactory = new configuration.validatorFactory(this.injector);
			if (configuration.crossValidator) {
				crossValidators.push(...factory.validators());
				crossAsyncValidators.push(...factory.asyncValidators());
			}
			else {
				controls[key] = new FormControl(
					configuration.default,
					factory.validators(),
					factory.asyncValidators());
			}
		}
		this.form = new FormGroup(controls, crossValidators, crossAsyncValidators);
		this._isChanging = new BehaviorSubject<boolean>(this.form.value);
		this._status = new BehaviorSubject<string>(this.form.status);
		this.form.statusChanges.subscribe({
			next: (status: string) => this._status.next(status),
			error: (error: Error) => this.logger.error(error)
		});
		this.form.valueChanges.subscribe({
			next: (values: any) => this._isChanging.next(values),
			error: (error: Error) => this.logger.error(error)
		});
	}

	public control(key: string): AbstractControl {
		return this.rawForm().controls[key];
	}

	public errors(key: string): FormErrorMessage[] {
		const specificationErrors = this.specification[key].errors;
		const augmentedErrors: FormErrorMessage[] = [];
		let controlErrors: ValidationErrors = undefined;
		if (this.specification[key].crossValidator) {
			if (this.form.invalid) {
				controlErrors = this.form.errors;
			}
		}
		else {
			if (this.form.controls[key].invalid) {
				controlErrors = this.form.controls[key].errors;
			}
		}
		if (controlErrors !== undefined) {
			for (const error in controlErrors) {
				const specificationError = specificationErrors
					.find(specificationError => specificationError.type === error);
				if (specificationError !== undefined) {
					const augmentedMessage: string = this
						.interpolate(specificationError.message, specificationError.type, controlErrors[error]);
					augmentedErrors.push({
						message: augmentedMessage,
						payload: controlErrors[error],
						type: specificationError.type
					});
				}
			}
		}
		return augmentedErrors;
	}

	public disable(): AdvancedForm {
		this.rawForm().disable();
		return this;
	}

	public enable(): AdvancedForm {
		this.rawForm().enable();
		return this;
	}

	public hasError(key: string, type: string): boolean {
		return this.form.controls[key].hasError(type);
	}

	public isChanging(): Observable<any> {
		return this._isChanging.asObservable();
	}

	public isDisabled(): Observable<boolean> {
		return this._status.asObservable()
			.pipe(map(status => status === "DISABLED"));
	}

	public isInvalid(): Observable<boolean> {
		return this._status.asObservable()
			.pipe(map(status => status === "INVALID"));
	}

	public isPending(): Observable<boolean> {
		return this._status.asObservable()
			.pipe(map(status => status === "PENDING"));
	}

	public isValid(): Observable<boolean> {
		return this._status.asObservable()
			.pipe(map(status => status === "VALID"));
	}

	/**
	 * @description
	 *	Almacena el estado actual del formulario en un DTO especificado. Si un
	 *	control del formulario posee un valor indefinido o es la cadena vacía,
	 *	entonces no se almacena.
	 *
	 * @param dto
	 *	El objeto sobre el cual almacenar el estado del formulario.
	 *
	 * @returns
	 *	El mismo DTO de entrada.
	 */
	public load<T>(dto: T): T {
		for (const [key, control] of Object.entries(this.form.controls)) {
			control.markAsTouched();
			if (control.value !== undefined
				&& control.value !== null
				&& control.value !== "") {
				const converter = this.converters.get(key);
				if (converter === undefined) {
					this.logger.warn(`Can't find a converter for the control '${key}'.`);
					dto[key] = control.value;
				}
				else {
					dto[key] = converter.postConvert(control.value);
				}
			}
        }
		return dto;
	}

	/**
	 * @description
	 *	Computa un observable que emite los cambios netos sobre el formulario,
	 *	es decir, siempre que se modifique un valor en particular, que un
	 *	nuevo campo sea creado, o que un campo existente sea destruido. Esto
	 *	permite responder siempre que el payload asociado al formulario cambie
	 *	realmente.
	 *
	 * @returns
	 *	Un observable con el estado anterior, la diferencia de estado (delta),
	 *	y un conjunto de claves de propiedades que fueron modificadas de
	 *	alguna manera.
	 */
	public netChange(): Observable<NetChange> {
		return this._isChanging.asObservable()
			.pipe(
				pairwise(),
				map(([oldState, newState]) => {
					const netChange: NetChange = {
						delta: {},
						keys: new Set<string>(),
						state: oldState
					};
					// Hallar propiedades que cambiaron o que ya no existen:
					for (const [oldKey, oldValue] of Object.entries(oldState)) {
						if (oldValue !== newState[oldKey]) {
							netChange.delta[oldKey] = newState[oldKey];
							netChange.keys.add(oldKey);
						}
					}
					// Hallar propiedades nuevas:
					for (const [newKey, newValue] of Object.entries(newState)) {
						if (!netChange.keys.has(newKey) && newValue !== oldState[newKey]) {
							netChange.delta[newKey] = newValue;
							netChange.keys.add(newKey);
						}
					}
					return netChange;
				}),
				filter(netChange => 0 < netChange.keys.size)
			);
	}

	/**
	 * @description
	 *	Análogo al observable 'netChange()', pero emitiendo únicamente el
	 *	cambio neto sobre la clave especificada.
	 *
	 * @param key
	 *	La clave a observar sobre cambios netos.
	 *
	 * @returns
	 *	Un observable sobre el cambio neto de la clave especificada.
	 */
	public netChangeOver(key: string): Observable<NetChange> {
		return this.netChange()
			.pipe(
				filter(netChange => netChange.keys.has(key)),
				map(netChange => {
					const netChangeOverKey: NetChange = {
						delta: {},
						keys: new Set<string>(),
						state: {}
					};
					netChangeOverKey.delta[key] = netChange.delta[key];
					netChangeOverKey.keys.add(key);
					netChangeOverKey.state[key] = netChange.state[key];
					return netChangeOverKey;
				})
			);
	}

	/**
	 * @description
	 *	Análogo al observable 'netChangeOver()', pero emitiendo únicamente el
	 *	cambio neto sobre la clave especificada si la misma existe en el
	 *	estado actual del formulario. Si no existe, entonces nunca se emite
	 *	ningún evento.
	 *
	 * @param key
	 *	La clave a observar sobre cambios netos.
	 *
	 * @returns
	 *	Un observable sobre el cambio neto de la clave especificada.
	 */
	public netChangeIfPresent(key: string): Observable<any> {
		return this.netChangeOver(key)
			.pipe(
				map(netChange => Optional.of(netChange.delta[key])),
				filter(optional => optional.isPresent()),
				map(optional => optional.value)
			);
	}

	public patch(values: { [key: string]: any }): AdvancedForm {
		this.form.patchValue(values, {
			onlySelf: false,
			emitEvent: true
		});
		for (const key of Object.keys(values)) {
			const control = this.form.controls[key];
			control.markAsTouched();
		}
		return this;
	}

	/**
	 * @description
	 *	Aplica una serie de actualizaciones en el orden especificado. Este
	 *	método es útil cuando se desea garantizar el orden de ejecución de
	 *	ciertos eventos subscriptos al estado del formulario.
	 *
	 * @param patches
	 *	Una lista de actualizaciones a aplicar (parches).
	 *
	 * @returns
	 *	Esta misma instancia.
	 */
	public patchInOrder(patches: { [key: string]: any }[]): AdvancedForm {
		patches.forEach(patch => this.patch(patch));
		return this;
	}

	/**
	 * @description
	 *	Devuelve el formulario interno que contiene la lógica real de
	 *	funcionamiento. Su uso debería estar limitado a pasar por argumento la
	 *	instancia en una vista HTML para indicar el formulario sobre el cual
	 *	realizar el binding de Angular, pero nada más.
	 *
	 * @returns
	 *	El formulario interno (legacy).
	 */
	public rawForm(): FormGroup {
		return this.form;
	}

	/**
	 * @description
	 *	Obtiene el valor presente del control especificado.
	 *
	 * @param key
	 *	La clave que identifica el control.
	 *
	 * @returns
	 *	El valor presente del control, en el instantte consultado.
	 */
	public valueOf(key: string): any {
		return this.control(key).value;
	}

	private interpolate(message: string, errorType: string, errorPayload: any): string {
		let interpolated = message;
		for (const [key, value] of Object.entries(errorPayload)) {
			interpolated = interpolated.replace(`{{${errorType}.${key}}}`, `${value}`);
		}
		return interpolated;
	}
}

	/**
	 * @description
	 *	Patrón builder para construir un formulario avanzado.
	 */

export class AdvancedFormBuilder {

	public _injector: Injector;
	public _logger: Logger;
	public _specification: FormSpecification;

	constructor() {
		this._logger = undefined;
		this._specification = undefined;
	}

	public build(): AdvancedForm {
		return new AdvancedForm(this);
	}

	public injector(injector: Injector): AdvancedFormBuilder {
		this._injector = injector;
		return this;
	}

	public logger(logger: Logger): AdvancedFormBuilder {
		this._logger = logger;
		return this;
	}

	public specification(specification: FormSpecification): AdvancedFormBuilder {
		if (specification === null || specification === undefined) {
			throw new Error(
				"La especificación del formulario no puede ser nula o indefinida.");
		}
		else {
			this._specification = specification;
			return this;
		}
	}
}
